<script setup lang="ts">
import {nextTick, onMounted, ref, watch} from "vue";
import {tools} from "@/utils/katex-tool.ts";
import CustomDialog from "components/dialog/CustomDialog.vue";

const toolbar = ref<HTMLElement>();
const renderDom = ref<HTMLElement>();
const isShowing = ref(true);
const content = ref("");
const container = ref<HTMLElement>();
const isCollapsed = ref(true); // 新增：控制收起状态

// 拖拽相关变量
const isDragging = ref(false);
const dragStartX = ref(0);
const dragStartY = ref(0);
const windowPosition = ref({x: 0, y: 0});

// 对话框相关变量
const dialogVisible = ref(false);
const selectedValue = ref("");

// 更新渲染内容
const onChange = () => {
  if (renderDom.value) {
    renderDom.value.innerText = content.value;
    renderMathInElement(renderDom.value);
  }
};

// 打开对话框
const onAddDialog = (value: string) => {
  selectedValue.value = value;
  dialogVisible.value = true;
};

// 添加公式
const onAdd = (type: "inline" | "block") => {
  let value = selectedValue.value;
  if (type === "inline") {
    value = `\\(${value}\\)`;
  } else {
    value = `$$${value}$$`;
  }
  content.value += value;
  onChange();
  dialogVisible.value = false; // 关闭对话框
};

// 拖拽开始
const startDrag = (e: MouseEvent) => {
  isDragging.value = true;
  dragStartX.value = e.clientX - windowPosition.value.x;
  dragStartY.value = e.clientY - windowPosition.value.y;
  window.addEventListener("mousemove", onDrag);
  window.addEventListener("mouseup", stopDrag);
};

// 拖拽中
const onDrag = (e: MouseEvent) => {
  if (isDragging.value) {
    windowPosition.value.x = e.clientX - dragStartX.value;
    windowPosition.value.y = e.clientY - dragStartY.value;
  }
};

// 拖拽结束
const stopDrag = () => {
  isDragging.value = false;
  window.removeEventListener("mousemove", onDrag);
  window.removeEventListener("mouseup", stopDrag);
};

onMounted(() => {
  nextTick(() => {
    if (toolbar.value) {
      renderMathInElement(toolbar.value);
    }
  });
});

// 监听 isCollapsed 的变化
watch(isCollapsed, (newVal) => {
  if (!newVal) {
    nextTick(() => {
      if (toolbar.value) {
        // 打开编辑器时重新渲染 toolbar
        renderMathInElement(toolbar.value);
      }
      if (renderDom.value) {
        renderDom.value.innerText = content.value;
        renderMathInElement(renderDom.value);
      }
    });
  }
});

defineExpose({isShowing});
</script>

<template>
  <div v-if="isShowing">
    <!-- 收起按钮 -->
    <div
        v-if="isCollapsed"
        class="collapse-button"
        @click="isCollapsed = false"
    >
      📝 打开公式预览器
    </div>

    <!-- 浮动窗口 -->
    <div
        v-else
        ref="container"
        class="floating-window h-620px"
        :style="{ left: `${windowPosition.x}px`, top: `${windowPosition.y}px` }"
    >
      <div class="drag-handle"
           @mousedown="startDrag"
           @mousemove="onDrag"
           @mouseup="stopDrag">
        拖动我
        <button class="collapse-icon" @click="isCollapsed = true">🗙 收起</button>
      </div>
      <div class="text-lg select-none">
        <div>使用 KaTeX 规则编写公式，<span class="font-bold">仅用于题目标题</span>！</div>
        <div>1. 行内公式: 以 \( 开始 并以 \) 结尾；注意<span class="font-bold">不能识别 $ 开始 $ 结尾的行内公式, 请自行替换！</span></div>
        <div>2. 行间公式: 以 $$ 开始 并以 $$ 结尾，行内在显示部分高的公式时会显示不完整，可使用行间公式</div>
        <div>3. 完整规则可参考：<a href="https://katex.org/docs/supported" target="_blank">KaTeX</a> 及 <a href="https://mhchem.github.io/MathJax-mhchem/" target="_blank">化学方程式等扩展</a></div>
        <div>4. 从别处复制的公式，请按以上规则进行替换，如果没有行间/行内的标记，请自行按需添加</div>
        <div>5. 工具栏仅提供案例，请插入模板后自行替换，如需要更多常用公式模板，反馈后进行更新</div>
      </div>
      <div class="flex gap-.5rem mt-.5rem select-none" ref="toolbar">
        <div class="bg-light-8 p-.5rem cursor-pointer rounded" v-for="item in tools" @click="onAddDialog(item.value)">
          {{ item.preview }}
        </div>
      </div>
      <div class="w-full h-auto flex gap-.5rem mt-.5rem">
        <el-input
            size="large"
            ref="inputContainer"
            type="textarea"
            resize="none"
            v-model="content"
            @input="onChange"
            class="flex-1"
            rows="16"
            placeholder="在此处输入公式文本，右侧显示预览"
        ></el-input>
        <div ref="renderDom" class="flex-1 text-lg h-350px overflow-y-auto"></div>
      </div>
    </div>

    <!-- 自定义对话框 -->
    <transition name="fade">
      <custom-dialog v-model:visible="dialogVisible" title="添加公式">
        <div class="flex flex-col gap-1">
          <div class="text-lg">请选择公式类型</div>
          <div class="flex gap-1">
            <div class="bg-light-8 p-1 cursor-pointer rounded" @click="onAdd('inline')">
              行内公式
            </div>
            <div class="bg-light-8 p-1 cursor-pointer rounded" @click="onAdd('block')">
              行间公式
            </div>
          </div>
        </div>
      </custom-dialog>
    </transition>
  </div>
</template>

<style scoped>
/* 收起按钮样式 */
.collapse-button {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background-color: #007bff;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  z-index: 9999;
}

/* 浮动窗口样式 */
.floating-window {
  position: fixed;
  z-index: 9999;
  background-color: white;
  border: 1px solid #ccc;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 1rem;
  border-radius: 8px;
  width: 800px;
  max-width: 90vw;
}

.drag-handle {
  cursor: move;
  background-color: #f0f0f0;
  padding: 0.5rem;
  border-bottom: 1px solid #ccc;
  text-align: center;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.collapse-icon {
  background-color: #ff4d4f;
  color: white;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
}

/* 过渡动画样式 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
